/** The folders a picker offers ("Move to…", where a share goes): searched by name, recent ones first. */

import type { SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { crumbsOf, type FolderIndex, isWithin, pathText, recentFolders } from "../../lib/folders.ts";
import { fold } from "../../lib/text.ts";
import { FolderIcon, NAV_ICONS } from "../../ui/icons.tsx";

/** The picker's option for the space's items, in no folder. */
export const ROOT = "root";
/** Folders the picker offers at once. */
const OFFERS = 30;

/** The folders offered for `q`: recent ones first (nothing typed), then by name; never `away` or what's in them. */
export function folderOffers(index: FolderIndex | undefined, space: SpaceOut, q: string, away: readonly string[]) {
  const needle = fold(q.trim());
  const ok = (id: string) => !away.some((a) => isWithin(index, id, a));
  const rows = (index?.rows ?? []).filter((f) => ok(f.id) && (!needle || fold(f.name).includes(needle)));
  const recent = needle ? [] : recentFolders(space).filter((id) => rows.some((f) => f.id === id));
  const ordered = [...recent, ...rows.map((f) => f.id).filter((id) => !recent.includes(id))].slice(0, OFFERS);
  const root = {
    value: ROOT,
    label: t("folders.root"),
    hint: t("folders.rootHint"),
    icon: <NAV_ICONS.items size={14} />,
  };
  return [
    ...(needle ? [] : [root]),
    ...ordered.map((id) => {
      const crumbs = crumbsOf(index, id);
      const above = pathText(crumbs.slice(0, -1));
      return {
        value: id,
        label: index?.byId.get(id)?.name ?? "",
        hint: recent.includes(id)
          ? t("folders.recent", { path: above || t("folders.root") })
          : above || t("folders.root"),
        icon: <FolderIcon size={14} />,
      };
    }),
  ];
}
